import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { config } from '~/config';
import { STATUS } from '~/literals';
import { RootState } from '~/types';
import { requestItineraryPayment } from '~/store/slices/itinerary-wizard';
import { trackButtonClick } from '~/utils/analytics';

const isRecord = (value: unknown): value is Record<string, any> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasValidPaymentGatewayPostData = (value: unknown): value is Record<string, any> => isRecord(value) && Object.keys(value).length > 0;

const getPaymentMethod = (payment: Record<string, any>) => String(payment.method || 'post').toLowerCase();

const ItineraryPayment = () => {
  const { register } = useForm<Record<string, any>>();
  const dispatch = useDispatch();
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const paymentRequest = useSelector((state: RootState) => state.itineraryWizard?.paymentRequest);
  const payment = paymentRequest?.payment || paymentRequest;
  const status = useSelector((state: RootState) => state.itineraryWizard?.pageStatus || STATUS.IDLE);
  const paymentError = useSelector((state: RootState) => state.itineraryWizard?.errorMessage || '');
  const paymentErrorMessage = typeof paymentError === 'string' ? paymentError : '';
  const isValidPaymentResponse = Boolean(
    isRecord(payment)
    && typeof payment.paymentGatewayRedirectUrl === 'string'
    && payment.paymentGatewayRedirectUrl.trim()
    && (
      getPaymentMethod(payment) === 'get'
      || hasValidPaymentGatewayPostData(payment.paymentGatewayPostData)
    ),
  );
  const hasInvalidPaymentResponse = status === STATUS.SUCCESS && Boolean(payment) && !isValidPaymentResponse;

  useEffect(() => {
    if (!isValidPaymentResponse || paymentError || !isRecord(payment)) {
      return;
    }

    if (getPaymentMethod(payment) === 'get') {
      window.location.assign(payment.paymentGatewayRedirectUrl);
      return;
    }

    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  }, [isValidPaymentResponse, payment, paymentError]);

  const handleRetry = () => {
    trackButtonClick('ItineraryPayment', 'Try again');

    dispatch(requestItineraryPayment());
  };

  return (
    <Box>
      {status === STATUS.RUNNING ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1">Please wait</Typography>
          <CircularProgress />
        </Box>
      ) : null}

      {status === STATUS.SUCCESS && isValidPaymentResponse && isRecord(payment) ? (
        <Box component='form' action={payment.paymentGatewayRedirectUrl} method={payment.method || 'post'}>
          {Object.keys(payment.paymentGatewayPostData || {}).map((key) => (
            <input
              key={key}
              type="hidden"
              {...register(key)}
              value={payment.paymentGatewayPostData[key]}
            />
          ))}
          <button type='submit' ref={submitButtonRef} style={{ display: 'none' }}>Submit</button>
        </Box>
      ) : null}

      {(paymentErrorMessage || status === STATUS.ERROR || hasInvalidPaymentResponse) && status !== STATUS.RUNNING ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {paymentErrorMessage || (
              <>
                We couldn&apos;t start your payment at the moment due to a temporary issue. Please try again in a few moments. If the problem continues,{' '}
                <Link href={`${config.app.helpCenterLink}/ask-for-help`} target="_blank" rel="noopener noreferrer">
                  contact our support team
                </Link>
                {' '}and we&apos;ll be happy to help.
              </>
            )}
          </Alert>
          <Button
            variant="contained"
            onClick={handleRetry}
          >
            Try again
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default ItineraryPayment;
